import createClient from 'openapi-fetch'
import { useDevError } from '@/composables/useDevError'
import { fetchWithRefresh } from '@/services/network/with_refresh'
import type { paths, components } from '@/types/api'

// Кастомный fetch-provider: каждый запрос автоматически получает TTFB- и
// total-таймауты (см. smart_fetch.ts) — не нужно дублировать на уровне
// конкретных вызовов. signal из useApiRequest проходит насквозь через
// Request → smartFetch комбинирует его с таймаутами через AbortSignal.any().
// Поверх — fetchWithRefresh: 401 → прозрачный refresh + повтор запроса (E5).
const client = createClient<paths>({
  baseUrl: '/api/v1',
  credentials: 'include',
  fetch: (request) => fetchWithRefresh(request),
})

// DEV-only: на любую 5xx-ошибку API кладём ответ в shared ref —
// DevErrorOverlay в App.vue показывает класс/сообщение/стек прямо на
// странице. В прод-сборке import.meta.env.DEV === false, middleware
// просто не подключается — оверлей никогда не появится у юзеров.
if (import.meta.env.DEV) {
  client.use({
    async onResponse({ request, response }) {
      if (response.status < 500) return
      const cloned = response.clone()
      const body = await cloned.text().catch(() => '<не удалось прочитать тело>')
      useDevError().set({
        status: response.status,
        url: request.url,
        method: request.method,
        body,
        contentType: response.headers.get('content-type') ?? '',
        receivedAt: new Date()
      })
    }
  })
}

// 401 → fetchWithRefresh пробует прозрачный refresh + повтор (E5). Если
// сессия не восстановилась — emitSessionExpired уводит на /login (обработчик
// регистрируется в entrypoints/application.ts: очистка стора + router.replace).
// При первичной загрузке (auth ещё не initialized) редирект делает
// router.beforeEach по auth.user == null — двойного перехода нет.

// Тип ошибки API: { error, messages?, message?, details? }
export interface ApiError {
  error: string
  messages?: string[]
  message?: string
  /** Ошибки по полям 422: ключ — путь поля запроса (`visit.planned_at`). */
  details?: Record<string, string[]>
}

// Удобный экстрактор сообщения об ошибке для UI
export function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'error' in err) {
    const e = err as ApiError
    if (e.messages && e.messages.length > 0) return e.messages.join(', ')
    if (e.message) return e.message
    return e.error
  }
  if (err instanceof Error) return err.message
  return 'Произошла ошибка'
}

// Ошибки по полям из 422 (`details`). Канала нет (доменная ошибка, 409, сеть) —
// пустая карта: форма показывает общую строку из errorMessage(). Тело приходит
// с сервера, поэтому форму значений проверяем в рантайме, а не только типами.
export function fieldErrors(err: unknown): Record<string, string[]> {
  if (err === null || typeof err !== 'object') return {}
  const bag = (err as { details?: unknown }).details
  if (bag === null || typeof bag !== 'object') return {}

  const result: Record<string, string[]> = {}
  for (const [field, value] of Object.entries(bag as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue
    const messages = value.filter((message): message is string => typeof message === 'string')
    if (messages.length > 0) result[field] = messages
  }
  return result
}

export default client

// Удобные type-aliases для компонентов/stores (уезжают в dead code при билде).
export type Schemas = components['schemas']
export type User = Schemas['User']
// Строка админ-списка: подмножество User (полный профиль — только у карточки).
export type UserListItem = Schemas['UserListItem']
